/**
 * As dimensões reais de cada imagem, medidas pelo navegador.
 *
 * POR QUE PRECISA DISSO
 *
 * O `resolve` escala a assinatura preservando a proporção, e para isso lê
 * `w`/`h` do asset. No servidor as medidas vêm do arquivo. No navegador, não
 * existem até a imagem carregar — e o manifesto do preview declarava zero.
 *
 * Zero não é "não sei": `ALTURA / 0` é Infinity, `0 * Infinity` é NaN, e a
 * primitiva saía com coordenada NaN. A assinatura desaparecia do preview sem
 * um erro sequer no console.
 *
 * Com a medida certa, o preview e o PDF escalam a mesma imagem do mesmo jeito.
 * Sem ela, o `resolve` usa uma proporção de reserva — a assinatura aparece,
 * mas com a forma errada, e os dois desenhos discordam. Discordar é a doença
 * que este pipeline existe para curar, então vale medir.
 */

import { useEffect, useState } from "react";

export type Dimensoes = ReadonlyMap<string, { w: number; h: number }>;

const VAZIO: Dimensoes = new Map();

/**
 * Mede cada URL uma vez e devolve o mapa.
 *
 * Enquanto uma medida não chega, a URL fica ausente do mapa — e o `resolve`
 * cai na proporção de reserva, que desenha algo em vez de nada.
 */
export function useDimensoesDeImagem(urls: readonly string[]): Dimensoes {
  const [medidas, setMedidas] = useState<Dimensoes>(VAZIO);

  // A chave estável: um array novo a cada render dispararia o efeito para
  // sempre, e cada disparo criaria uma imagem nova.
  const chave = urls.filter(Boolean).sort().join("\n");

  useEffect(() => {
    const lista = chave ? chave.split("\n") : [];
    if (lista.length === 0) {
      setMedidas((atual) => (atual.size === 0 ? atual : VAZIO));
      return;
    }

    let vivo = true;
    const parciais = new Map<string, { w: number; h: number }>();
    let pendentes = lista.length;

    const concluir = () => {
      if (!vivo) return;
      pendentes -= 1;
      // Um `setState` por lote, e não um por imagem: cada um re-resolve o
      // certificado inteiro.
      if (pendentes === 0) setMedidas(new Map(parciais));
    };

    for (const url of lista) {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          parciais.set(url, { w: img.naturalWidth, h: img.naturalHeight });
        }
        concluir();
      };
      // Falha de carga não trava o lote: sem medida, a proporção de reserva
      // vale, e o produtor vê a imagem quebrada — que é o problema real dele.
      img.onerror = concluir;
      img.src = url;
    }

    return () => {
      vivo = false;
    };
  }, [chave]);

  return medidas;
}
